import type { TradingSignals } from '../../schemas/indicatortypes.ts';

// Trading Signals Generation
export class TradingSignalsGenerator {
    /**
     * Generate trading signals based on market analysis
     */
    static generateSignals(
        currentPrice: number,
        trueVWAP: number,
        momentum5: number,
        momentum10: number,
        sessionReturn: number,
        currentVolume: number,
        avgVolume: number,
        pricePosition: number,
        volatility: number,
    ): { bullishSignals: number; bearishSignals: number; signals: string[] } {
        let bullishSignals = 0;
        let bearishSignals = 0;
        const signals: string[] = [];

        // Signal 1: Price vs VWAP
        if (currentPrice > trueVWAP) {
            signals.push(
                `✓ BULLISH: Price above VWAP (+${(((currentPrice - trueVWAP) / trueVWAP) * 100).toFixed(2)}%)`,
            );
            bullishSignals++;
        } else {
            signals.push(`✗ BEARISH: Price below VWAP (${(((currentPrice - trueVWAP) / trueVWAP) * 100).toFixed(2)}%)`);
            bearishSignals++;
        }

        // Signal 2: Momentum
        if (momentum5 > 0 && momentum10 > 0) {
            signals.push('✓ BULLISH: Positive momentum on both timeframes');
            bullishSignals++;
        } else if (momentum5 < 0 && momentum10 < 0) {
            signals.push('✗ BEARISH: Negative momentum on both timeframes');
            bearishSignals++;
        } else {
            signals.push('◐ MIXED: Conflicting momentum signals');
        }

        // Signal 3: Volume confirmation
        const volumeRatio = currentVolume / avgVolume;

        if (volumeRatio > 1.2 && sessionReturn > 0) {
            signals.push('✓ BULLISH: Above-average volume supporting upward move');
            bullishSignals++;
        } else if (volumeRatio > 1.2 && sessionReturn < 0) {
            signals.push('✗ BEARISH: Above-average volume supporting downward move');
            bearishSignals++;
        } else {
            signals.push('◐ NEUTRAL: Volume not providing clear direction');
        }

        // Signal 4: Position in range and volatility
        if (pricePosition > 65 && volatility > 30) {
            signals.push('✗ BEARISH: High in range with elevated volatility - reversal risk');
            bearishSignals++;
        } else if (pricePosition < 35 && volatility > 30) {
            signals.push('✓ BULLISH: Low in range with volatility - potential bounce');
            bullishSignals++;
        } else {
            signals.push('◐ NEUTRAL: Price position and volatility not extreme');
        }

        return { bullishSignals, bearishSignals, signals };
    }

    /**
     * Calculate overall signal and confidence
     */
    static calculateOverallSignal(
        bullishSignals: number,
        bearishSignals: number,
        signals: string[],
        volatility: number,
    ): TradingSignals {
        const totalScore = bullishSignals - bearishSignals;
        const overallSignal = totalScore > 0 ? 'BULLISH_BIAS' : totalScore < 0 ? 'BEARISH_BIAS' : 'NEUTRAL';
        const confidence = TradingSignalsGenerator.calculateConfidence(signals);
        const riskLevel = TradingSignalsGenerator.calculateRiskLevel(volatility);

        return {
            bullishSignals,
            bearishSignals,
            signals,
            overallSignal,
            signalScore: totalScore,
            confidence,
            riskLevel,
        };
    }

    /**
     * Calculate confidence based on number of signals
     */
    private static calculateConfidence(signals: string[]): number {
        return Math.min(signals.length * 10, 100);
    }

    /**
     * Calculate risk level based on volatility
     */
    private static calculateRiskLevel(volatility: number): string {
        if (volatility < 20) return 'LOW';
        if (volatility < 40) return 'MEDIUM';
        return 'HIGH';
    }
}
